import React, { useState, useEffect } from 'react';
import { useOrgAuth } from '../../../contexts/OrgAuthContext';
import {
  getFinancialCaseQueue, claimFinancialCase, releaseFinancialClaim,
  approveFinancialCase, declineFinancialCase, getFinancialHistory,
  getAwardCycles, createAwardCycle, updateAwardCycleStatus,
  getAwardNominations, adjudicateNomination
} from '../../../api/programmesApi';

const TABS = [
  { key: 'queue', label: 'Hardship Case Queue' },
  { key: 'history', label: 'Case History' },
  { key: 'awards', label: 'Award Cycles' },
  { key: 'nominations', label: 'Nominations Review' },
  { key: 'reports', label: 'Reports' },
];

const PAYMENT_METHODS = ['Cash', 'PayNow', 'Cheque'];

const fmtDate = (ts) => ts ? new Date(ts?.toDate?.() || ts).toLocaleDateString('en-SG') : '—';

function daysSince(ts) {
  if (!ts) return 0;
  const d = new Date(ts?.toDate?.() || ts);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function SlaCell({ days }) {
  if (days >= 5) return <span style={{ color: '#DC2626', fontWeight: 600 }}>⚠️ {days}d</span>;
  return <span>{days}d</span>;
}

const STATUS_BADGE = {
  pending: { cls: 'bb-badge bb-badge--gray', label: 'Pending' },
  claimed: { cls: 'bb-badge', style: { background: '#DBEAFE', color: '#1E40AF' }, label: 'In Review' },
  approved: { cls: 'bb-badge bb-badge--green', label: 'Approved' },
  declined: { cls: 'bb-badge', style: { background: '#FEE2E2', color: '#991B1B' }, label: 'Declined' },
};

const CYCLE_STATUS_BADGE = {
  draft: { cls: 'bb-badge bb-badge--gray', label: 'Draft' },
  open: { cls: 'bb-badge bb-badge--green', label: 'Open' },
  closed: { cls: 'bb-badge', style: { background: '#FEE2E2', color: '#991B1B' }, label: 'Closed' },
};

export default function FinancialAssistance({ orgId }) {
  const { currentUser } = useOrgAuth();
  const [activeTab, setActiveTab] = useState('queue');

  // Case Queue
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesError, setCasesError] = useState('');
  const [expandedCase, setExpandedCase] = useState(null);
  const [caseUpdating, setCaseUpdating] = useState({});
  const [approveForm, setApproveForm] = useState({ amount: '', paymentMethod: 'PayNow', notes: '' });
  const [declineForm, setDeclineForm] = useState({ reason: '', altSuggestion: '' });
  const [approveTarget, setApproveTarget] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);

  // Case History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyFilterStatus, setHistoryFilterStatus] = useState('');
  const [historyFilterFrom, setHistoryFilterFrom] = useState('');
  const [historyFilterTo, setHistoryFilterTo] = useState('');

  // Award Cycles
  const [cycles, setCycles] = useState([]);
  const [cyclesLoading, setCyclesLoading] = useState(false);
  const [cyclesError, setCyclesError] = useState('');
  const [showAddCycle, setShowAddCycle] = useState(false);
  const [cycleForm, setCycleForm] = useState({ year: new Date().getFullYear(), openDate: '', closeDate: '' });
  const [cycleSubmitting, setCycleSubmitting] = useState(false);
  const [cycleUpdating, setCycleUpdating] = useState({});

  // Nominations
  const [nominations, setNominations] = useState([]);
  const [nominationsLoading, setNominationsLoading] = useState(false);
  const [nominationsError, setNominationsError] = useState('');
  const [nomCycleFilter, setNomCycleFilter] = useState('');
  const [expandedNom, setExpandedNom] = useState(null);
  const [awardModal, setAwardModal] = useState(null);
  const [awardForm, setAwardForm] = useState({ amount: '', type: 'cash', ceremonyDetails: '' });
  const [notSelectedModal, setNotSelectedModal] = useState(null);
  const [notSelectedNotes, setNotSelectedNotes] = useState('');
  const [nomUpdating, setNomUpdating] = useState({});

  useEffect(() => {
    if (activeTab === 'queue') loadCases();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'awards') loadCycles();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'nominations') { loadCycles(); loadNominations(); }
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'reports') { loadHistory(); loadNominations(); }
  }, [activeTab, orgId]);

  const loadCases = async () => {
    setCasesLoading(true); setCasesError('');
    try { setCases(await getFinancialCaseQueue(orgId) || []); }
    catch (e) { setCasesError(e.message || 'Failed to load case queue.'); }
    finally { setCasesLoading(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true); setHistoryError('');
    try { setHistory(await getFinancialHistory(orgId) || []); }
    catch (e) { setHistoryError(e.message || 'Failed to load history.'); }
    finally { setHistoryLoading(false); }
  };

  const loadCycles = async () => {
    setCyclesLoading(true); setCyclesError('');
    try { setCycles(await getAwardCycles(orgId) || []); }
    catch (e) { setCyclesError(e.message || 'Failed to load award cycles.'); }
    finally { setCyclesLoading(false); }
  };

  const loadNominations = async () => {
    setNominationsLoading(true); setNominationsError('');
    try { setNominations(await getAwardNominations(orgId) || []); }
    catch (e) { setNominationsError(e.message || 'Failed to load nominations.'); }
    finally { setNominationsLoading(false); }
  };

  const handleClaim = async (caseId) => {
    setCaseUpdating(p => ({ ...p, [caseId]: true }));
    try { await claimFinancialCase(orgId, caseId, currentUser.uid); await loadCases(); }
    catch (e) { setCasesError(e.message || 'Failed to claim case.'); }
    finally { setCaseUpdating(p => ({ ...p, [caseId]: false })); }
  };

  const handleRelease = async (caseId) => {
    setCaseUpdating(p => ({ ...p, [caseId]: true }));
    try { await releaseFinancialClaim(orgId, caseId); await loadCases(); }
    catch (e) { setCasesError(e.message || 'Failed to release case.'); }
    finally { setCaseUpdating(p => ({ ...p, [caseId]: false })); }
  };

  const handleApproveSubmit = async () => {
    if (!approveForm.amount) { setCasesError('Approval amount is required.'); return; }
    setCaseUpdating(p => ({ ...p, [approveTarget]: true }));
    try {
      await approveFinancialCase(orgId, approveTarget, {
        amount: parseFloat(approveForm.amount),
        paymentMethod: approveForm.paymentMethod,
        notes: approveForm.notes,
        approvedBy: currentUser.uid
      });
      setApproveTarget(null); setApproveForm({ amount: '', paymentMethod: 'PayNow', notes: '' });
      setExpandedCase(null); setCasesError('');
      await loadCases();
    } catch (e) { setCasesError(e.message || 'Failed to approve case.'); }
    finally { setCaseUpdating(p => ({ ...p, [approveTarget]: false })); }
  };

  const handleDeclineSubmit = async () => {
    if (!declineForm.reason.trim()) { setCasesError('Decline reason is required.'); return; }
    setCaseUpdating(p => ({ ...p, [declineTarget]: true }));
    try {
      await declineFinancialCase(orgId, declineTarget, {
        reason: declineForm.reason,
        altSuggestion: declineForm.altSuggestion,
        declinedBy: currentUser.uid
      });
      setDeclineTarget(null); setDeclineForm({ reason: '', altSuggestion: '' });
      setExpandedCase(null); setCasesError('');
      await loadCases();
    } catch (e) { setCasesError(e.message || 'Failed to decline case.'); }
    finally { setCaseUpdating(p => ({ ...p, [declineTarget]: false })); }
  };

  const handleAddCycle = async (e) => {
    e.preventDefault(); setCycleSubmitting(true); setCyclesError('');
    try {
      await createAwardCycle(orgId, { ...cycleForm, status: 'draft', createdBy: currentUser.uid });
      setShowAddCycle(false); setCycleForm({ year: new Date().getFullYear(), openDate: '', closeDate: '' });
      await loadCycles();
    } catch (e) { setCyclesError(e.message || 'Failed to create award cycle.'); }
    finally { setCycleSubmitting(false); }
  };

  const handleCycleStatus = async (cycleId, newStatus) => {
    if (newStatus === 'open' && cycles.some(c => c.status === 'open' && c.id !== cycleId)) {
      setCyclesError('Only one award cycle can be open at a time. Close the current open cycle first.');
      return;
    }
    setCycleUpdating(p => ({ ...p, [cycleId]: true }));
    try {
      await updateAwardCycleStatus(orgId, cycleId, newStatus, { updatedBy: currentUser.uid });
      await loadCycles();
    } catch (e) { setCyclesError(e.message || 'Failed to update cycle status.'); }
    finally { setCycleUpdating(p => ({ ...p, [cycleId]: false })); }
  };

  const handleAwardSubmit = async () => {
    if (!awardForm.amount) { setNominationsError('Award amount is required.'); return; }
    setNomUpdating(p => ({ ...p, [awardModal]: true }));
    try {
      await adjudicateNomination(orgId, awardModal, 'awarded', {
        amount: parseFloat(awardForm.amount),
        type: awardForm.type,
        ceremonyDetails: awardForm.ceremonyDetails,
        adjudicatedBy: currentUser.uid
      });
      setAwardModal(null); setAwardForm({ amount: '', type: 'cash', ceremonyDetails: '' });
      setNominationsError('');
      await loadNominations();
    } catch (e) { setNominationsError(e.message || 'Failed to award nomination.'); }
    finally { setNomUpdating(p => ({ ...p, [awardModal]: false })); }
  };

  const handleNotSelectedSubmit = async () => {
    setNomUpdating(p => ({ ...p, [notSelectedModal]: true }));
    try {
      await adjudicateNomination(orgId, notSelectedModal, 'not_selected', {
        internalNotes: notSelectedNotes,
        adjudicatedBy: currentUser.uid
      });
      setNotSelectedModal(null); setNotSelectedNotes('');
      await loadNominations();
    } catch (e) { setNominationsError(e.message || 'Failed to update nomination.'); }
    finally { setNomUpdating(p => ({ ...p, [notSelectedModal]: false })); }
  };

  const filteredHistory = history.filter(c => {
    if (historyFilterStatus && c.status !== historyFilterStatus) return false;
    if (historyFilterFrom) {
      const from = new Date(historyFilterFrom);
      const d = new Date(c.createdAt?.toDate?.() || c.createdAt);
      if (d < from) return false;
    }
    if (historyFilterTo) {
      const to = new Date(historyFilterTo);
      const d = new Date(c.createdAt?.toDate?.() || c.createdAt);
      if (d > to) return false;
    }
    return true;
  });

  const filteredNominations = nominations.filter(n => !nomCycleFilter || n.cycleId === nomCycleFilter);

  // Report stats
  const thisYear = new Date().getFullYear();
  const totalDisbursed = history.filter(c => c.status === 'approved' && new Date(c.approvedAt?.toDate?.() || c.approvedAt).getFullYear() === thisYear)
    .reduce((sum, c) => sum + (c.approvedAmount || 0), 0);
  const approvedCount = history.filter(c => c.status === 'approved').length;
  const declinedCount = history.filter(c => c.status === 'declined').length;
  const nomTotal = nominations.length;
  const nomAwarded = nominations.filter(n => n.status === 'awarded').length;
  const resolvedCases = history.filter(c => c.status === 'approved' || c.status === 'declined');
  const avgProcessingDays = resolvedCases.length
    ? Math.round(resolvedCases.reduce((sum, c) => sum + (daysSince(c.createdAt) || 0), 0) / resolvedCases.length)
    : 0;

  return (
    <div className="bb-editor">
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', marginBottom: 24, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            borderBottom: activeTab === t.key ? '2px solid #6B46C1' : '2px solid transparent',
            color: activeTab === t.key ? '#6B46C1' : '#6B7280',
            fontWeight: activeTab === t.key ? 600 : 400, fontSize: 13
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── HARDSHIP CASE QUEUE ── */}
      {activeTab === 'queue' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Hardship Case Queue</h2>
            <p className="bb-editor-desc">SLA-critical queue for financial assistance applications. Cases open 5+ days are flagged.</p>
          </div>
          {casesError && <div className="bb-alert bb-alert--error">{casesError}</div>}

          {/* Approve Modal */}
          {approveTarget && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Approve Financial Assistance</h3>
                <div className="bb-form-row">
                  <div className="bb-form-group">
                    <label>Amount (SGD) <span className="bb-required">*</span></label>
                    <input className="bb-input" type="number" min={0} step={0.01} value={approveForm.amount} onChange={e => setApproveForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" />
                  </div>
                  <div className="bb-form-group">
                    <label>Payment Method</label>
                    <select className="bb-select" value={approveForm.paymentMethod} onChange={e => setApproveForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                      {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div className="bb-form-group">
                  <label>Admin Notes</label>
                  <textarea className="bb-textarea" rows={3} value={approveForm.notes} onChange={e => setApproveForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes on the approval decision." />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-primary" disabled={caseUpdating[approveTarget]} onClick={handleApproveSubmit}>Confirm Approval</button>
                  <button className="bb-btn-outline-sm" onClick={() => { setApproveTarget(null); setApproveForm({ amount: '', paymentMethod: 'PayNow', notes: '' }); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Decline Modal */}
          {declineTarget && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Decline Hardship Case</h3>
                <div className="bb-form-group">
                  <label>Reason <span className="bb-required">*</span></label>
                  <textarea className="bb-textarea" rows={3} value={declineForm.reason} onChange={e => setDeclineForm(p => ({ ...p, reason: e.target.value }))} placeholder="Explain why this application is being declined." />
                </div>
                <div className="bb-form-group">
                  <label>Suggested Alternative <span className="bb-hint">(optional)</span></label>
                  <input className="bb-input" value={declineForm.altSuggestion} onChange={e => setDeclineForm(p => ({ ...p, altSuggestion: e.target.value }))} placeholder="e.g. Refer to ComCare" />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} disabled={caseUpdating[declineTarget]} onClick={handleDeclineSubmit}>Confirm Decline</button>
                  <button className="bb-btn-outline-sm" onClick={() => { setDeclineTarget(null); setDeclineForm({ reason: '', altSuggestion: '' }); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {casesLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={7}>Loading cases…</td></tr></tbody></table>
          ) : cases.length === 0 ? (
            <div className="bb-empty-state">No cases in the queue.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Member</th><th>Description</th><th>Submitted</th><th>Days Open</th><th>Claimed By</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cases.map(c => {
                  const days = daysSince(c.createdAt);
                  const badge = STATUS_BADGE[c.status] || STATUS_BADGE.pending;
                  const isMine = c.claimedBy === currentUser.uid;
                  const isOpen = c.status !== 'approved' && c.status !== 'declined';
                  const isExpanded = expandedCase === c.id;
                  return (
                    <React.Fragment key={c.id}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedCase(isExpanded ? null : c.id)}>
                        <td><strong>{c.memberName || c.memberId}</strong></td>
                        <td style={{ fontSize: 12, color: '#6B7280', maxWidth: 200 }}>
                          {c.description ? c.description.slice(0, 80) + (c.description.length > 80 ? '…' : '') : '—'}
                        </td>
                        <td>{fmtDate(c.createdAt)}</td>
                        <td><SlaCell days={days} /></td>
                        <td>{c.claimedByName || (c.claimedBy ? c.claimedBy : <span style={{ color: '#9CA3AF' }}>—</span>)}</td>
                        <td><span className={badge.cls} style={badge.style}>{badge.label}</span></td>
                        <td style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                          {isOpen && !c.claimedBy && <button className="bb-btn-sm" disabled={caseUpdating[c.id]} onClick={() => handleClaim(c.id)}>Claim</button>}
                          {isOpen && isMine && <button className="bb-btn-outline-sm" disabled={caseUpdating[c.id]} onClick={() => handleRelease(c.id)}>Release</button>}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} style={{ background: '#F9FAFB', padding: 0 }}>
                            <div style={{ padding: 20, borderLeft: '3px solid #6B46C1' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                <div>
                                  <strong style={{ fontSize: 13 }}>Full Description</strong>
                                  <p style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{c.description || '—'}</p>
                                </div>
                                <div>
                                  <strong style={{ fontSize: 13 }}>Household Info</strong>
                                  <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                                    {c.householdSize ? <div>Household size: {c.householdSize}</div> : null}
                                    {c.monthlyIncome !== undefined ? <div>Monthly income: SGD {c.monthlyIncome}</div> : null}
                                    {!c.householdSize && c.monthlyIncome === undefined && <div style={{ color: '#9CA3AF' }}>No household data recorded.</div>}
                                  </div>
                                </div>
                                <div>
                                  <strong style={{ fontSize: 13 }}>Documents</strong>
                                  {c.documents && c.documents.length > 0
                                    ? <ul style={{ fontSize: 13, marginTop: 4, paddingLeft: 16 }}>{c.documents.map((d, i) => <li key={i}>{d}</li>)}</ul>
                                    : <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>No documents listed.</div>
                                  }
                                </div>
                                <div>
                                  <strong style={{ fontSize: 13 }}>Previous Assistance</strong>
                                  <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                                    {c.previousAssistance ? c.previousAssistance : <span style={{ color: '#9CA3AF' }}>No prior assistance recorded.</span>}
                                  </div>
                                </div>
                              </div>
                              {isOpen && isMine && (
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button className="bb-btn-primary" onClick={() => { setApproveTarget(c.id); setApproveForm({ amount: '', paymentMethod: 'PayNow', notes: '' }); }}>Approve</button>
                                  <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} onClick={() => { setDeclineTarget(c.id); setDeclineForm({ reason: '', altSuggestion: '' }); }}>Decline</button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── CASE HISTORY ── */}
      {activeTab === 'history' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Case History</h2>
            <p className="bb-editor-desc">All historical financial assistance cases.</p>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="bb-form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>Status</label>
              <select className="bb-select" value={historyFilterStatus} onChange={e => setHistoryFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="approved">Approved</option>
                <option value="declined">Declined</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="bb-form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>From</label>
              <input className="bb-input" type="date" value={historyFilterFrom} onChange={e => setHistoryFilterFrom(e.target.value)} />
            </div>
            <div className="bb-form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>To</label>
              <input className="bb-input" type="date" value={historyFilterTo} onChange={e => setHistoryFilterTo(e.target.value)} />
            </div>
          </div>

          <div className="bb-stats-row" style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <div className="bb-stat-card">
              <div className="bb-stat-value">SGD {totalDisbursed.toLocaleString('en-SG', { minimumFractionDigits: 2 })}</div>
              <div className="bb-stat-label">Total Disbursed (This Year)</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{avgProcessingDays}d</div>
              <div className="bb-stat-label">Avg Processing Days</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{approvedCount > 0 ? Math.round(approvedCount / (approvedCount + declinedCount) * 100) : 0}%</div>
              <div className="bb-stat-label">Approval Rate</div>
            </div>
          </div>

          {historyError && <div className="bb-alert bb-alert--error">{historyError}</div>}
          {historyLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={6}>Loading history…</td></tr></tbody></table>
          ) : filteredHistory.length === 0 ? (
            <div className="bb-empty-state">No cases found for the selected filters.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Member</th><th>Submitted</th><th>Status</th><th>Amount (SGD)</th><th>Payment Method</th><th>Processed By</th></tr></thead>
              <tbody>
                {filteredHistory.map(c => {
                  const badge = STATUS_BADGE[c.status] || STATUS_BADGE.pending;
                  return (
                    <tr key={c.id}>
                      <td>{c.memberName || c.memberId}</td>
                      <td>{fmtDate(c.createdAt)}</td>
                      <td><span className={badge.cls} style={badge.style}>{badge.label}</span></td>
                      <td>{c.approvedAmount ? `$${c.approvedAmount.toFixed(2)}` : '—'}</td>
                      <td>{c.paymentMethod || '—'}</td>
                      <td>{c.approvedByName || c.approvedBy || c.declinedBy || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── AWARD CYCLES ── */}
      {activeTab === 'awards' && (
        <div>
          <div className="bb-editor-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0 }}>Award Cycles</h2>
              <p className="bb-editor-desc">Manage annual scholarship and award nomination cycles. Only one cycle can be open at a time.</p>
            </div>
            <button className="bb-btn-primary" onClick={() => setShowAddCycle(v => !v)}>{showAddCycle ? 'Cancel' : '+ Create Cycle'}</button>
          </div>
          {cyclesError && <div className="bb-alert bb-alert--error">{cyclesError}</div>}
          {showAddCycle && (
            <form onSubmit={handleAddCycle} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 24, marginBottom: 24 }}>
              <div className="bb-section-title" style={{ marginBottom: 16 }}>New Award Cycle</div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Year <span className="bb-required">*</span></label>
                  <input className="bb-input" type="number" required min={2020} max={2099} value={cycleForm.year} onChange={e => setCycleForm(p => ({ ...p, year: e.target.value }))} />
                </div>
                <div className="bb-form-group">
                  <label>Open Date</label>
                  <input className="bb-input" type="date" value={cycleForm.openDate} onChange={e => setCycleForm(p => ({ ...p, openDate: e.target.value }))} />
                </div>
                <div className="bb-form-group">
                  <label>Close Date</label>
                  <input className="bb-input" type="date" value={cycleForm.closeDate} onChange={e => setCycleForm(p => ({ ...p, closeDate: e.target.value }))} />
                </div>
              </div>
              <div className="bb-editor-actions">
                <button className="bb-btn-primary" type="submit" disabled={cycleSubmitting}>{cycleSubmitting ? 'Creating…' : 'Create Cycle'}</button>
                <button className="bb-btn-outline-sm" type="button" onClick={() => setShowAddCycle(false)}>Cancel</button>
              </div>
            </form>
          )}
          {cyclesLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={7}>Loading cycles…</td></tr></tbody></table>
          ) : cycles.length === 0 ? (
            <div className="bb-empty-state">No award cycles created yet.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Year</th><th>Status</th><th>Open Date</th><th>Close Date</th><th>Nominations</th><th>Actions</th></tr></thead>
              <tbody>
                {cycles.map(cy => {
                  const badge = CYCLE_STATUS_BADGE[cy.status] || CYCLE_STATUS_BADGE.draft;
                  const nomCount = nominations.filter(n => n.cycleId === cy.id).length;
                  return (
                    <tr key={cy.id}>
                      <td><strong>{cy.year}</strong></td>
                      <td><span className={badge.cls} style={badge.style}>{badge.label}</span></td>
                      <td>{fmtDate(cy.openDate)}</td>
                      <td>{fmtDate(cy.closeDate)}</td>
                      <td>{nomCount}</td>
                      <td style={{ display: 'flex', gap: 4 }}>
                        {cy.status === 'draft' && <button className="bb-btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} disabled={cycleUpdating[cy.id]} onClick={() => handleCycleStatus(cy.id, 'open')}>Open</button>}
                        {cy.status === 'open' && <button className="bb-btn-danger-sm" disabled={cycleUpdating[cy.id]} onClick={() => handleCycleStatus(cy.id, 'closed')}>Close</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── NOMINATIONS REVIEW ── */}
      {activeTab === 'nominations' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Nominations Review</h2>
            <p className="bb-editor-desc">Review and adjudicate award nominations.</p>
          </div>
          {nominationsError && <div className="bb-alert bb-alert--error">{nominationsError}</div>}

          {awardModal !== null && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Award Nomination</h3>
                <div className="bb-form-row">
                  <div className="bb-form-group">
                    <label>Amount (SGD) <span className="bb-required">*</span></label>
                    <input className="bb-input" type="number" min={0} step={0.01} value={awardForm.amount} onChange={e => setAwardForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" />
                  </div>
                  <div className="bb-form-group">
                    <label>Award Type</label>
                    <select className="bb-select" value={awardForm.type} onChange={e => setAwardForm(p => ({ ...p, type: e.target.value }))}>
                      <option value="cash">Cash</option>
                      <option value="voucher">Voucher</option>
                      <option value="certificate">Certificate</option>
                    </select>
                  </div>
                </div>
                <div className="bb-form-group">
                  <label>Ceremony Details <span className="bb-hint">(optional)</span></label>
                  <textarea className="bb-textarea" rows={2} value={awardForm.ceremonyDetails} onChange={e => setAwardForm(p => ({ ...p, ceremonyDetails: e.target.value }))} placeholder="Date, venue, or presentation notes." />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-primary" disabled={nomUpdating[awardModal]} onClick={handleAwardSubmit}>Confirm Award</button>
                  <button className="bb-btn-outline-sm" onClick={() => { setAwardModal(null); setAwardForm({ amount: '', type: 'cash', ceremonyDetails: '' }); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {notSelectedModal !== null && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Mark as Not Selected</h3>
                <div className="bb-form-group">
                  <label>Internal Notes <span className="bb-hint">(optional)</span></label>
                  <textarea className="bb-textarea" rows={3} value={notSelectedNotes} onChange={e => setNotSelectedNotes(e.target.value)} placeholder="Internal reasoning for this decision (not shared with applicant)." />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} disabled={nomUpdating[notSelectedModal]} onClick={handleNotSelectedSubmit}>Confirm</button>
                  <button className="bb-btn-outline-sm" onClick={() => { setNotSelectedModal(null); setNotSelectedNotes(''); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <select className="bb-select" style={{ width: 240 }} value={nomCycleFilter} onChange={e => setNomCycleFilter(e.target.value)}>
              <option value="">All Cycles</option>
              {cycles.map(cy => <option key={cy.id} value={cy.id}>{cy.year} ({cy.status})</option>)}
            </select>
          </div>

          {nominationsLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={7}>Loading nominations…</td></tr></tbody></table>
          ) : filteredNominations.length === 0 ? (
            <div className="bb-empty-state">No nominations found.</div>
          ) : (
            filteredNominations.map(nom => {
              const isExpanded = expandedNom === nom.id;
              const isPending = nom.status === 'pending';
              return (
                <div key={nom.id} className="bb-card" style={{ marginBottom: 12 }}>
                  <div className="bb-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpandedNom(isExpanded ? null : nom.id)}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <strong>{nom.memberName || nom.memberId}</strong>
                      {nom.childName && <span style={{ fontSize: 13, color: '#6B7280' }}>Child: {nom.childName}{nom.childAge ? `, age ${nom.childAge}` : ''}</span>}
                      <span className={nom.status === 'awarded' ? 'bb-badge bb-badge--green' : nom.status === 'not_selected' ? 'bb-badge bb-badge--gray' : 'bb-badge'} style={nom.status === 'pending' ? { background: '#FEF9C3', color: '#92400E' } : undefined}>
                        {nom.status === 'awarded' ? 'Awarded' : nom.status === 'not_selected' ? 'Not Selected' : 'Pending'}
                      </span>
                    </div>
                    <span style={{ color: '#6B7280', fontSize: 12 }}>{fmtDate(nom.submittedAt || nom.createdAt)}</span>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: '16px 20px', borderTop: '1px solid #E5E7EB' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                        <div>
                          <strong style={{ fontSize: 13 }}>Academic Results</strong>
                          <p style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{nom.academicResults || '—'}</p>
                        </div>
                        <div>
                          <strong style={{ fontSize: 13 }}>Supporting Statement</strong>
                          <p style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{nom.statement || '—'}</p>
                        </div>
                        {nom.documents && nom.documents.length > 0 && (
                          <div>
                            <strong style={{ fontSize: 13 }}>Documents</strong>
                            <ul style={{ fontSize: 13, marginTop: 4, paddingLeft: 16 }}>{nom.documents.map((d, i) => <li key={i}>{d}</li>)}</ul>
                          </div>
                        )}
                      </div>
                      {isPending && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="bb-btn-primary" disabled={nomUpdating[nom.id]} onClick={() => { setAwardModal(nom.id); setAwardForm({ amount: '', type: 'cash', ceremonyDetails: '' }); }}>Award</button>
                          <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} disabled={nomUpdating[nom.id]} onClick={() => { setNotSelectedModal(nom.id); setNotSelectedNotes(''); }}>Not Selected</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── REPORTS ── */}
      {activeTab === 'reports' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Reports</h2>
            <p className="bb-editor-desc">Financial assistance programme statistics and outcomes.</p>
          </div>
          <div className="bb-section-title" style={{ marginBottom: 12 }}>Hardship Fund — {thisYear}</div>
          <div className="bb-stats-row" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
            <div className="bb-stat-card">
              <div className="bb-stat-value">SGD {totalDisbursed.toLocaleString('en-SG', { minimumFractionDigits: 2 })}</div>
              <div className="bb-stat-label">Total Disbursed</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{approvedCount}</div>
              <div className="bb-stat-label">Cases Approved</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{declinedCount}</div>
              <div className="bb-stat-label">Cases Declined</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{avgProcessingDays}d</div>
              <div className="bb-stat-label">Avg Processing Days</div>
            </div>
          </div>
          <div className="bb-section-title" style={{ marginBottom: 12 }}>Award Nominations</div>
          <div className="bb-stats-row" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{nomTotal}</div>
              <div className="bb-stat-label">Nominations Received</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{nomAwarded}</div>
              <div className="bb-stat-label">Nominations Awarded</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{nomTotal > 0 ? Math.round(nomAwarded / nomTotal * 100) : 0}%</div>
              <div className="bb-stat-label">Award Rate</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
